import { useState, useCallback, useRef, useEffect } from 'react';

import { api } from '../contexts/AuthContext';

/**
 * Extract error message from various error formats
 * Supports: string detail, Pydantic array errors, network errors, timeout errors
 *
 * @param {Error} error - The error object
 * @param {string} fallbackMessage - Default message if no specific error found
 * @returns {string} - Extracted error message in Korean
 */
export const extractErrorMessage = (error, fallbackMessage = '요청 중 오류가 발생했습니다.') => {
  // Check for network/timeout errors (set by AuthContext interceptor)
  if (error.code === 'NETWORK_ERROR') {
    return '네트워크 연결을 확인해주세요.';
  }
  if (error.code === 'TIMEOUT_ERROR') {
    return '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
  }

  // Check for response data
  if (!error.response?.data) {
    return error.message || fallbackMessage;
  }

  const { detail, message } = error.response.data;

  // Handle string detail (most common from FastAPI)
  if (typeof detail === 'string') {
    return detail;
  }

  // Handle Pydantic validation errors (array format)
  if (Array.isArray(detail)) {
    const parsed = detail
      .map(item => item?.msg || item)
      .filter(Boolean)
      .join(', ');
    if (parsed) return parsed;
  }

  // Handle object detail with message property
  if (detail?.message) {
    return detail.message;
  }

  // Handle direct message field
  if (typeof message === 'string') {
    return message;
  }

  return fallbackMessage;
};

/**
 * Custom hook for API calls with standardized loading/error handling
 *
 * @example
 * // Basic usage
 * const { data, loading, error, execute } = useApiCall();
 *
 * // With options
 * const { data, loading, error, execute, reset } = useApiCall({
 *   onSuccess: (data) => console.log('Success:', data),
 *   onError: (error, message) => console.error('Error:', message),
 *   initialData: [],
 *   errorMessage: '데이터를 불러올 수 없습니다.',
 * });
 *
 * // Execute API call
 * await execute((api) => api.get('/api/v1/endpoint'));
 *
 * @param {Object} options - Hook configuration options
 * @param {Function} options.onSuccess - Callback on successful response
 * @param {Function} options.onError - Callback on error (receives error and message)
 * @param {*} options.initialData - Initial value for data state
 * @param {string} options.errorMessage - Fallback error message
 * @returns {Object} - { data, loading, error, execute, reset, clearError, setData }
 */
const useApiCall = (options = {}) => {
  const {
    initialData = null,
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track if component is mounted to prevent state updates after unmount
  const mountedRef = useRef(true);

  // Track the current in-flight AbortController
  const currentControllerRef = useRef(null);

  // Latest options ref — updated every render so execute always reads the latest values
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Abort any in-flight request when component unmounts
      if (currentControllerRef.current) {
        currentControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Execute an API call with automatic loading/error handling
   *
   * @param {Function} requestFn - Function that receives api instance and an options object
   *   containing a signal. Callers can opt-in to abort support:
   *   execute((api, { signal }) => api.get('/endpoint', { signal }))
   *   Legacy single-arg callers are unaffected.
   * @param {Object} executeOptions - Per-call options
   * @param {boolean} executeOptions.abortPrevious - If true, abort the previous in-flight
   *   request before starting a new one (default: false)
   * @returns {Promise<{success: boolean, data?: any, error?: Error, message?: string, aborted?: boolean}>}
   */
  const execute = useCallback(async (requestFn, executeOptions = {}) => {
    const { abortPrevious = false } = executeOptions;

    // Read the latest options from the ref — always up-to-date without being a dep
    const {
      onSuccess,
      onError,
      errorMessage = '요청 중 오류가 발생했습니다.',
    } = optionsRef.current;

    // Optionally abort the previous in-flight request (race condition defence)
    if (abortPrevious && currentControllerRef.current) {
      currentControllerRef.current.abort();
    }

    const controller = new AbortController();
    currentControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      // Pass signal as second arg; legacy callers that ignore it are unaffected
      const response = await requestFn(api, { signal: controller.signal });

      // Only update state if still mounted AND request was not aborted
      if (mountedRef.current && !controller.signal.aborted) {
        setData(response.data);
        if (onSuccess) {
          onSuccess(response.data);
        }
      }

      return { success: true, data: response.data };
    } catch (err) {
      // AbortError / axios CanceledError = intentional cancellation — do NOT touch state
      if (err.name === 'AbortError' || err.name === 'CanceledError' || controller.signal.aborted) {
        return { success: false, aborted: true };
      }

      const message = extractErrorMessage(err, errorMessage);

      if (mountedRef.current) {
        setError(message);
        if (onError) {
          onError(err, message);
        }
      }

      return { success: false, error: err, message };
    } finally {
      // Skip setLoading(false) when aborted to avoid stale state flicker
      if (mountedRef.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []); // execute identity is permanently stable — options read via optionsRef

  /**
   * Reset all states to initial values
   */
  const reset = useCallback(() => {
    setData(initialData);
    setLoading(false);
    setError(null);
  }, [initialData]);

  /**
   * Clear only the error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset,
    clearError,
    setData, // Allow direct data manipulation for optimistic updates
  };
};

export default useApiCall;
